import {weakMemo} from '@packages/utilities/memo';
import {emptyArray, emptyObject, concatTo} from '@packages/utilities/fp/utils';
import {
  withMeta,
  withSystem,
  getSystem,
  getMeta,
  Plugins,
  Component,
  Types,
  Expressions,
} from '@packages/systems/wfdl';
import {getName, qualify} from '@packages/systems/wfdl-Qualified';
import type {Type} from '@packages/systems/wfdl';

import {TRecord, type ExpressionElement} from '@packages/systems/wfdl-ast';
import {getStyleVariantTypeArguments} from '@packages/systems/component-style-variants';

// Add isDefault in order to avoid creating
// intermediary objects.
const addIsDefaultFlag = (field: Type, isDefault: boolean): Type => {
  if (!field) {
    return field;
  }

  const system = getSystem(field);

  if (system && system.isDefault === isDefault) {
    return field;
  }

  return withSystem({...system, isDefault}, field);
};

const resumeWithEmptyArray = Expressions.resume(emptyArray);

export const getBoundFieldIds = Expressions.everything<string[]>(concatTo)((
  expr
) => {
  const select = Expressions.getSelect(expr);
  return select && Expressions.isDataVariable(select.from)
    ? Expressions.resume([select.prop])
    : resumeWithEmptyArray;
});

export const isStyleVariantUnlinked = (
  field: Type,
  componentMap: Component.ComponentMap,
  options: {
    isOwnedStyleVariant?: boolean;
    isReferencedInRender?: boolean;
  } = {}
) => {
  if (options.isOwnedStyleVariant) {
    return false;
  }

  const {variantFieldId, componentName, pluginName} =
    getStyleVariantTypeArguments(field);
  if (!variantFieldId || !componentName || !pluginName) {
    return true;
  }
  const componentWithVariant = componentMap.get(
    qualify(pluginName, componentName)
  );
  if (!componentWithVariant) {
    return true;
  }
  const variants = Component.getVariants(componentWithVariant);
  const variantKeys = variants ? Object.keys(variants) : [];
  if (!variantKeys.includes(variantFieldId)) {
    return true;
  }
  return options.isReferencedInRender === false;
};

/**
 * Given a component root, return a version of `dataType` that is sorted by the
 * order that each binding appears in the dataType of the component with the updated isUnlinked flag.
 */
export const getSortedDataTypeFields = weakMemo(
  (componentRoot: Component.ComponentType) =>
    weakMemo((componentMap: Component.ComponentMap) => {
      const dataType = Component.getDataType(componentRoot);
      const dataTypeFields = Types.getRecordFields(dataType);

      if (!dataTypeFields) {
        return emptyObject;
      }

      const sortedDataTypeFields: Record<string, Type> = {};

      const boundFieldIds = new Set<string>();

      for (const fieldId of getBoundFieldIds(
        Component.getRender(componentRoot)
      )) {
        const field = dataTypeFields[fieldId];
        if (field) {
          boundFieldIds.add(fieldId);
        }
      }
      const variants = Component.getVariants(componentRoot);
      const ownedStyleVariantFieldIds = variants
        ? new Set(Object.keys(variants))
        : null;

      for (const fieldId of Object.keys(dataTypeFields)) {
        const fieldDataType = dataTypeFields[fieldId];
        if (fieldDataType) {
          const isUnlinked = Types.isStyleVariantType(fieldDataType)
            ? isStyleVariantUnlinked(fieldDataType, componentMap, {
                isOwnedStyleVariant:
                  ownedStyleVariantFieldIds?.has(fieldId) ?? false,
                isReferencedInRender: boundFieldIds.has(fieldId),
              })
            : Types.isVariableModeType(fieldDataType)
              ? false
              : !boundFieldIds.has(fieldId);
          sortedDataTypeFields[fieldId] = withSystem(
            {
              ...getSystem(fieldDataType),
              isUnlinked,
            },
            fieldDataType
          );
        }
      }

      return sortedDataTypeFields;
    })
);

export const combineDataTypeFields = weakMemo(
  (componentFields: {[key: string]: Type}) =>
    weakMemo(<a>(props: {[key: string]: a}) =>
      Object.keys(componentFields).reduce<{
        [key: string]: Type;
      }>((result, fieldId) => {
        const componentRootField = componentFields[fieldId];

        // it looks like we can get corrupted data with some undefined fields here
        if (!componentRootField) {
          return result;
        }

        result[fieldId] = addIsDefaultFlag(
          componentRootField,
          !(fieldId in props)
        );
        return result;
      }, {})
    )
);

const buildComponentInstanceDataType = weakMemo(
  (component: Component.ComponentType) =>
    weakMemo(<a>(props: {[key: string]: a}) =>
      weakMemo((componentMap: Component.ComponentMap) => {
        const componentRootOverridesFields =
          getSortedDataTypeFields(component)(componentMap);
        const componentRootDataType = Component.getDataType(component);

        if (!componentRootOverridesFields) {
          throw new TypeError(
            `Unexpected symbol root overrides Type: ${Types.print(
              componentRootDataType
            )}`
          );
        }

        const fields = combineDataTypeFields(componentRootOverridesFields)(
          props
        );
        const record = TRecord(fields);
        const meta = componentRootDataType
          ? getMeta(componentRootDataType)
          : null;
        return meta ? withMeta(meta, record) : record;
      })
    )
);

export const getDataTypeFromElement = weakMemo((plugins: Plugins.PluginsType) =>
  weakMemo((componentMap: Component.ComponentMap) =>
    weakMemo((element: ExpressionElement): Type => {
      const componentRoot = componentMap.get(element.type);
      if (componentRoot) {
        const props =
          Expressions.getRecord(Expressions.getElementData(element)) ||
          emptyObject;
        return buildComponentInstanceDataType(componentRoot)(props)(
          componentMap
        );
      }

      // TODO qualified support to getAtomByType https://app.clickup.com/t/6mqm3n
      return Plugins.getAtomByType(plugins, getName(element.type)).dataType;
    })
  )
);
